import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { cn } from '@/lib/utils'
import type { SendDocumentRequest } from '@/types'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const dialogContentClass =
  'max-h-[calc(100vh-1.5rem)] max-w-[640px] gap-0 overflow-y-auto rounded-2xl border-[#e7e9ef] bg-white p-8 shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)] sm:rounded-2xl'

const outlineBtnClass = cn(
  'h-10 rounded-xl border-[#e7e9ef] bg-white px-3 text-sm font-medium leading-none text-[#171a20] normal-case hover:bg-[#fbfbfc]',
  FORM_CONTROL_FOCUS_CLASS
)
const primaryBtnClass = cn(
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white normal-case hover:bg-[#e6511c]',
  FORM_CONTROL_FOCUS_CLASS
)
const fieldInputClass = cn(
  'h-10 rounded-xl border border-transparent bg-[#f5f6fa] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)
const textareaClass = cn(
  'min-h-[200px] resize-y rounded-xl border border-transparent bg-[#f5f6fa] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)

// Field label with an optional red required marker, matching Figma 1085:12832.
function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor: string
  children: React.ReactNode
  required?: boolean
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'
    >
      <span>{children}</span>
      {required && <span className='text-[#d63b33]'>*</span>}
    </Label>
  )
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  // Default recipient (client primary email) + template subject/body.
  defaultTo?: string | null
  defaultSubject?: string
  defaultBody?: string
  // Show the additional-recipients field (invoice + credit note) (2.6.5-007).
  allowAdditional?: boolean
  // Show the e-signature checkbox (quote send only) (2.5.6-010).
  showSignature?: boolean
  signatureDefault?: boolean
  // Success toast text + the network send action.
  successMessage: string
  onSend: (
    body: SendDocumentRequest & { request_signature?: boolean }
  ) => Promise<void>
  // Fallback error text when the send fails.
  errorMessage?: string
}

// Shared Send-to-client/accountant popup for quotes (2.5.6), invoices (2.6.5),
// and credit notes (2.6.11). Validates recipient email format inline.
export function SendDocumentDialog({
  open,
  onOpenChange,
  title,
  defaultTo,
  defaultSubject = '',
  defaultBody = '',
  allowAdditional = false,
  showSignature = false,
  signatureDefault = false,
  successMessage,
  onSend,
  errorMessage,
}: Props) {
  const { t } = useTranslation('billing')
  const resolvedErrorMessage = errorMessage ?? t('sendDialog.errorMessage')
  const [to, setTo] = useState(defaultTo ?? '')
  const [additional, setAdditional] = useState('')
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState(defaultBody)
  const [signature, setSignature] = useState(signatureDefault)
  const [toError, setToError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (open) {
      // Reset the form to template/default values when the dialog opens.
      /* eslint-disable react-hooks/set-state-in-effect */
      setTo(defaultTo ?? '')
      setAdditional('')
      setSubject(defaultSubject)
      setBody(defaultBody)
      setSignature(signatureDefault)
      setToError(null)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [open, defaultTo, defaultSubject, defaultBody, signatureDefault])

  const submit = async () => {
    if (!to || !EMAIL_RE.test(to.trim())) {
      setToError(t('sendDialog.toInvalid'))
      return
    }
    const extras = additional
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
    if (extras.some(e => !EMAIL_RE.test(e))) {
      toast({
        variant: 'destructive',
        title: t('sendDialog.additionalInvalid'),
      })
      return
    }
    setSending(true)
    try {
      await onSend({
        recipient_email: to.trim(),
        recipient_emails: extras.length ? extras : undefined,
        message: body || undefined,
        request_signature: showSignature ? signature : undefined,
      })
      toast({ variant: 'success', title: successMessage })
      onOpenChange(false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, resolvedErrorMessage),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogContentClass}>
        <div className='flex flex-col gap-6'>
          <DialogTitle className='pr-8 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
            {title}
          </DialogTitle>

          {showSignature && (
            <label className='flex items-center gap-3 rounded-xl border border-[#e7e9ef] bg-[#fbfbfc] p-3 text-sm font-medium leading-[1.4] text-[#171a20]'>
              <Checkbox
                checked={signature}
                onCheckedChange={v => setSignature(!!v)}
                className={FORM_CONTROL_FOCUS_CLASS}
              />
              {t('sendDialog.requestSignature')}
            </label>
          )}

          <div className='space-y-1.5'>
            <FieldLabel htmlFor='send-to' required>
              {t('sendDialog.to')}
            </FieldLabel>
            <Input
              id='send-to'
              type='email'
              value={to}
              onChange={e => {
                setTo(e.target.value)
                setToError(null)
              }}
              className={cn(fieldInputClass, toError && 'border-destructive')}
              placeholder={t('sendDialog.toPlaceholder')}
            />
            {toError && (
              <p className='text-xs leading-4 text-destructive'>{toError}</p>
            )}
          </div>

          {allowAdditional && (
            <div className='space-y-1.5'>
              <FieldLabel htmlFor='send-additional'>
                {t('sendDialog.additionalRecipients')}
              </FieldLabel>
              <Input
                id='send-additional'
                value={additional}
                onChange={e => setAdditional(e.target.value)}
                className={fieldInputClass}
                placeholder={t('sendDialog.additionalPlaceholder')}
              />
            </div>
          )}

          <div className='space-y-1.5'>
            <FieldLabel htmlFor='send-subject' required>
              {t('sendDialog.subject')}
            </FieldLabel>
            <Input
              id='send-subject'
              value={subject}
              maxLength={200}
              onChange={e => setSubject(e.target.value)}
              className={fieldInputClass}
            />
          </div>

          <div className='space-y-1.5'>
            <FieldLabel htmlFor='send-body' required>
              {t('sendDialog.body')}
            </FieldLabel>
            <Textarea
              id='send-body'
              value={body}
              maxLength={5000}
              rows={6}
              onChange={e => setBody(e.target.value)}
              className={textareaClass}
            />
          </div>

          <p className='text-sm font-normal leading-[1.4] text-[#3d434e]'>
            {t('sendDialog.pdfAttachedNote')}
          </p>

          <div className='flex flex-col-reverse gap-3 sm:flex-row sm:justify-end'>
            <Button
              type='button'
              variant='outline'
              className={outlineBtnClass}
              disabled={sending}
              onClick={() => onOpenChange(false)}
            >
              {t('sendDialog.cancel')}
            </Button>
            <Button
              type='button'
              className={primaryBtnClass}
              disabled={sending}
              onClick={submit}
            >
              {sending && <Loader2 className='h-4 w-4 animate-spin' />}
              {t('sendDialog.send')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
